/**
 * @file galleryTypes.ts
 * @description Data-driven gallery contract (ratified decision #7), extended
 * by SE-6532 with design-source provenance.
 *
 * Two layers live here:
 *
 *  1. **The rendering contract** — `GalleryEntry` / `GalleryCase`. Every atom
 *     ships a `gallery.tsx` exporting one entry; `/playground` renders the
 *     generated manifest and lint asserts completeness.
 *  2. **The provenance contract** — every gallery case must resolve to either
 *     an exact Figma node (`mapped`) or an audited, ticket-linked statement
 *     that Figma does not specify it (`not-specified`). There is no third
 *     option and no default: a component with no provenance fails generation.
 *     Audited absence is deliberately first-class, because roughly nine
 *     percent of the instances in the handoff designs — and most of this
 *     repo's atoms — have no published Figma component, and that gap only
 *     gets fixed if it is recorded rather than assumed.
 * @module components/atoms/galleryTypes
 */
import type React from "react";

import semanticStates from "./gallerySemanticStates.json";

/** One named prop combination rendered in the gallery. */
export interface GalleryCase {
  /** Display label, e.g. "tone=danger size=sm". */
  readonly title: string;
  /** Props passed verbatim to the component. */
  readonly props: Record<string, unknown>;
  /** Optional children (string or element). */
  readonly children?: React.ReactNode;
  /**
   * Escape for compound/overlay atoms whose preview can't be expressed as
   * plain props (e.g. Modal content): renders instead of Component+props.
   */
  readonly render?: () => React.ReactNode;
}

/** Gallery manifest entry for one atom. */
export interface GalleryEntry {
  /** Atom name — must match the directory name. */
  readonly name: string;
  /** The atom component. */
  readonly Component: React.ComponentType<never>;
  /** Exhaustive named cases (closed enums → finite grid). */
  readonly cases: readonly GalleryCase[];
}

/** Tracker key format for every provenance record. */
export type GallerySourceTicket = `SE-${number}`;

/** The closed set of semantic states a screen-level gallery must account for. */
export const SEMANTIC_STATES = semanticStates.states as readonly string[];

/** Minimum length of an audited reason, so "n/a" cannot pass for an audit. */
export const MIN_AUDIT_REASON_LENGTH = 48;

/** Which layer of the design system a mapping was taken from. */
export type GalleryMappingBasis =
  /** A standalone component node in a canonical-tier component library (DPL or legacy). */
  | "component-library-node"
  /**
   * A standalone component node in the `feature-library` file (SE-8443) — bindable and
   * certified, but not a DPL node, and the registry says so rather than passing it off as one.
   */
  | "feature-library-node"
  /** An occurrence inside an authoritative screen/handoff frame. */
  | "authoritative-refinement-occurrence";

/** Whether a mapped node has a native Figma Code Connect mapping. */
export type GalleryCodeConnectStatus =
  /** A Code Connect mapping exists for the node. */
  | "mapped"
  /** Eligible (published to a team library) but not yet mapped. */
  | "eligible-unmapped"
  /** Not a Code Connect target: unpublished node, frame, or screen occurrence. */
  | "ineligible-unpublished";

/** Theme a Figma reference applies to. */
export type GalleryTheme = "light" | "dark" | "unspecified";

/** A pointer to one exact node in one exact Figma file. */
export interface GalleryFigmaNode {
  /** Figma file key, as it appears in the file URL. */
  readonly fileKey: string;
  /** Node id in `<number>:<number>` form. */
  readonly nodeId: string;
}

/** One themed Figma reference for a mapped design source. */
export interface GalleryFigmaReference {
  /** Theme this reference is authoritative for. */
  readonly theme: GalleryTheme;
  /** The referenced node. */
  readonly figma: GalleryFigmaNode;
}

/**
 * Record of a light/dark sweep against the design.
 *
 * `figmaThemeAxis` states what the design actually offers, which is not the
 * same as what was swept: a library with no dark variants can still be swept
 * in both themes, and saying so is how "dark is unverifiable against Figma"
 * stays visible instead of quietly reading as verified.
 */
export interface GalleryThemeSweep {
  /** Ticket that performed the sweep. */
  readonly ticket: GallerySourceTicket;
  /** Themes the implementation was captured in — must include both. */
  readonly sweptThemes: readonly ("light" | "dark")[];
  /** What the design supplies: no theme axis, one theme, or both. */
  readonly figmaThemeAxis: "none" | "single-theme" | "per-theme";
  /** Why the axis is what it is. */
  readonly reason: string;
}

/** A gallery case backed by an exact Figma node. */
export interface MappedGalleryDesignSource {
  /** Discriminant. */
  readonly status: "mapped";
  /** Stable kebab-case id that survives node replacement. */
  readonly id: string;
  /** Ticket that authored or last verified this mapping. */
  readonly sourceTicket: GallerySourceTicket;
  /** Which design-system layer the mapping was taken from. */
  readonly mappingBasis: GalleryMappingBasis;
  /** Page node the mapping was resolved within. */
  readonly authoritativePageNodeId: string;
  /** One reference per theme the mapping covers. */
  readonly figmaReferences: readonly GalleryFigmaReference[];
  /** Published component-set name, when the basis is a library node. */
  readonly componentSetName?: string;
  /** Native Code Connect state for the mapped node. */
  readonly codeConnectStatus: GalleryCodeConnectStatus;
  /** Light/dark sweep record. */
  readonly themeSweep: GalleryThemeSweep;
}

/**
 * A gallery case the design does not specify — audited, not assumed.
 *
 * `auditVersion` is the Figma **file version** at audit time, not a date, so
 * a stale audit can be invalidated mechanically when the file moves on.
 */
export interface NotSpecifiedGalleryDesignSource {
  /** Discriminant. */
  readonly status: "not-specified";
  /** Stable kebab-case id. */
  readonly id: string;
  /** Ticket that performed the audit. */
  readonly sourceTicket: GallerySourceTicket;
  /** Primary file that was searched — the declared component library. */
  readonly auditedFileKey: string;
  /**
   * Every library searched. An absence is only true relative to what was
   * looked at, so drift detection has to invalidate the audit when ANY of
   * them changes, not just the primary one.
   */
  readonly auditedFileKeys?: readonly string[];
  /** Figma file version string at audit time. */
  readonly auditVersion: string;
  /** ISO date (YYYY-MM-DD) the audit was performed. */
  readonly auditDate: string;
  /** Nodes that previously backed this case and have since been removed. */
  readonly retiredReferences?: readonly string[];
  /** Why no node was found. At least MIN_AUDIT_REASON_LENGTH characters. */
  readonly reason: string;
}

/** Provenance for one gallery case. */
export type GalleryDesignSource =
  | MappedGalleryDesignSource
  | NotSpecifiedGalleryDesignSource;

/** Coverage of one semantic state by a semantic gallery. */
export type SemanticStateCoverage =
  | {
      /** The state is rendered by at least one deterministic case. */
      readonly status: "covered";
      /** Ids of the cases that cover it. */
      readonly caseIds: readonly string[];
    }
  | {
      /** The design does not specify this state. */
      readonly status: "not-specified";
      /** File that was searched. */
      readonly auditedFileKey: string;
      /** Why the state is absent. At least MIN_AUDIT_REASON_LENGTH characters. */
      readonly reason: string;
    };

/** One deterministic case in a feature or screen gallery. */
export interface SemanticGalleryCase {
  /** Stable id, unique within the entry. */
  readonly id: string;
  /** Semantic state this case demonstrates. */
  readonly semanticState: string;
  /** Prose describing the offline fixture state. No network is permitted. */
  readonly deterministicSetup: string;
  /** Renders the case. */
  readonly render: () => React.ReactNode;
}

/** A feature-level or screen-level gallery entry. */
export interface SemanticGalleryEntry {
  /** Entry name — must match the directory name. */
  readonly name: string;
  /** Owning feature directory, e.g. "scouting". */
  readonly feature: string;
  /** Which layer this surface belongs to. */
  readonly tier: "feature-component" | "screen";
  /** Ticket that authored the gallery. */
  readonly sourceTicket: GallerySourceTicket;
  /** Figma node per theme, when the design specifies this surface. */
  readonly figmaByTheme: Readonly<
    Partial<Record<"light" | "dark", GalleryFigmaNode>>
  >;
  /** Coverage decision for every semantic state. */
  readonly stateMatrix: Readonly<Record<string, SemanticStateCoverage>>;
  /** Deterministic cases. */
  readonly cases: readonly SemanticGalleryCase[];
}

const KEBAB_RE = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;
const TICKET_RE = /^SE-\d+$/;
const NODE_ID_RE = /^\d+:\d+$/;
const ISO_DATE_RE = /^\d{4}-\d{2}-\d{2}$/;

/**
 * Throw with a contextual prefix when a contract rule is violated.
 * @param condition - The rule that must hold.
 * @param context - What is being validated, for the error message.
 * @param message - What went wrong.
 * @throws {Error} When condition is false.
 */
const require_ = (condition: boolean, context: string, message: string) => {
  if (!condition) throw new Error(`design-source ${context}: ${message}`);
};

/**
 * Validate the theme sweep attached to a mapped design source.
 *
 * The per-axis reference count is asserted **exactly**, not as a presence
 * check: a trailing `unspecified` reference would otherwise absorb either
 * theme's evidence and a single-theme design would silently read as covering
 * both.
 * @param sweep - The sweep record.
 * @param references - The mapped source's Figma references.
 * @param context - Identifier for error messages.
 * @throws {Error} When the sweep is inconsistent with its references.
 */
export const assertThemeSweep = (
  sweep: GalleryThemeSweep,
  references: readonly GalleryFigmaReference[],
  context: string
) => {
  const themes = references.map(r => r.theme);

  require_(
    TICKET_RE.test(sweep.ticket),
    context,
    `themeSweep.ticket "${sweep.ticket}" must look like SE-1234`
  );
  require_(
    sweep.sweptThemes.includes("light") && sweep.sweptThemes.includes("dark"),
    context,
    "themeSweep.sweptThemes must include both light and dark"
  );
  require_(
    sweep.reason.trim().length >= MIN_AUDIT_REASON_LENGTH,
    context,
    `themeSweep.reason must be at least ${MIN_AUDIT_REASON_LENGTH} characters`
  );

  if (sweep.figmaThemeAxis === "none") {
    require_(
      references.length === 1 && themes[0] === "unspecified",
      context,
      `figmaThemeAxis "none" requires exactly one reference with theme "unspecified", got [${themes.join(", ")}]`
    );
    return;
  }
  if (sweep.figmaThemeAxis === "single-theme") {
    require_(
      references.length === 1 &&
        (themes[0] === "light" || themes[0] === "dark"),
      context,
      `figmaThemeAxis "single-theme" requires exactly one light or dark reference, got [${themes.join(", ")}]`
    );
    return;
  }
  require_(
    sweep.figmaThemeAxis === "per-theme",
    context,
    `figmaThemeAxis "${sweep.figmaThemeAxis}" must be none, single-theme, or per-theme`
  );
  require_(
    references.length === 2 &&
      themes.includes("light") &&
      themes.includes("dark"),
    context,
    `figmaThemeAxis "per-theme" requires exactly two references, one light and one dark, got [${themes.join(", ")}]`
  );
};

/**
 * Validate one design-source record.
 * @param source - The record to validate.
 * @param context - Identifier for error messages (usually the entry name).
 * @returns The same record, for chaining.
 * @throws {Error} When the record violates the provenance contract.
 */
export const assertDesignSource = (
  source: GalleryDesignSource,
  context: string
): GalleryDesignSource => {
  require_(
    KEBAB_RE.test(source.id),
    context,
    `id "${source.id}" must be kebab-case`
  );
  require_(
    TICKET_RE.test(source.sourceTicket),
    context,
    `sourceTicket "${source.sourceTicket}" must look like SE-1234`
  );

  if (source.status === "mapped") {
    require_(
      source.figmaReferences.length > 0,
      context,
      "a mapped source needs at least one Figma reference"
    );
    for (const reference of source.figmaReferences) {
      require_(
        reference.figma.fileKey.length > 0,
        context,
        "every Figma reference needs a fileKey"
      );
      require_(
        NODE_ID_RE.test(reference.figma.nodeId),
        context,
        `nodeId "${reference.figma.nodeId}" must look like 1:23`
      );
    }
    require_(
      NODE_ID_RE.test(source.authoritativePageNodeId),
      context,
      `authoritativePageNodeId "${source.authoritativePageNodeId}" must look like 1:23`
    );
    assertThemeSweep(source.themeSweep, source.figmaReferences, context);
    return source;
  }

  // The registry is JSON reaching this validator through a type assertion, so
  // the discriminant is not guaranteed by the type system. Without this, a
  // typo'd status silently takes the audited-absence branch.
  require_(
    source.status === "not-specified",
    context,
    `status "${source.status}" must be "mapped" or "not-specified" — there is no default`
  );
  require_(
    source.auditedFileKey.length > 0,
    context,
    "an audited absence must name the file that was searched"
  );
  require_(
    source.auditVersion.length > 0,
    context,
    "an audited absence must record the Figma file version it was audited at"
  );
  require_(
    ISO_DATE_RE.test(source.auditDate),
    context,
    `auditDate "${source.auditDate}" must be an ISO date (YYYY-MM-DD)`
  );
  require_(
    source.reason.trim().length >= MIN_AUDIT_REASON_LENGTH,
    context,
    `reason must be at least ${MIN_AUDIT_REASON_LENGTH} characters — state what was searched and why nothing matched`
  );
  return source;
};

/**
 * Declare a feature-level or screen-level gallery, validating it at module
 * init so a malformed entry fails the import rather than rendering wrongly.
 * @param entry - The semantic gallery entry.
 * @returns The same entry, validated.
 * @throws {Error} When the entry violates the semantic contract.
 */
export const defineSemanticGallery = (
  entry: SemanticGalleryEntry
): SemanticGalleryEntry => {
  const context = `${entry.feature}/${entry.name}`;
  const caseIds = entry.cases.map(item => item.id);
  const duplicates = [
    ...new Set(caseIds.filter((id, index) => caseIds.indexOf(id) !== index)),
  ];
  const ids = new Set(caseIds);

  require_(
    TICKET_RE.test(entry.sourceTicket),
    context,
    `sourceTicket "${entry.sourceTicket}" must look like SE-1234`
  );
  require_(
    entry.cases.length > 0,
    context,
    "a gallery needs at least one case"
  );
  require_(
    duplicates.length === 0,
    context,
    `duplicate case id(s) "${duplicates.join('", "')}" — case ids must be unique within an entry`
  );

  for (const item of entry.cases) {
    require_(
      SEMANTIC_STATES.includes(item.semanticState),
      context,
      `case "${item.id}" declares unknown semantic state "${item.semanticState}"`
    );
    require_(
      item.deterministicSetup.trim().length > 0,
      context,
      `case "${item.id}" must describe its offline fixture state`
    );
  }

  for (const [theme, node] of Object.entries(entry.figmaByTheme)) {
    require_(
      node !== undefined && NODE_ID_RE.test(node.nodeId),
      context,
      `figmaByTheme.${theme} must point at a node id like 1:23`
    );
  }

  for (const state of SEMANTIC_STATES) {
    const coverage = entry.stateMatrix[state];
    require_(
      coverage !== undefined,
      context,
      `stateMatrix is missing semantic state "${state}" — cover it or audit it as not-specified`
    );
    if (coverage.status === "covered") {
      require_(
        coverage.caseIds.length > 0 &&
          coverage.caseIds.every(id => ids.has(id)),
        context,
        `stateMatrix."${state}" lists case ids that do not exist in this entry`
      );
    } else {
      require_(
        coverage.reason.trim().length >= MIN_AUDIT_REASON_LENGTH,
        context,
        `stateMatrix."${state}" audited reason must be at least ${MIN_AUDIT_REASON_LENGTH} characters`
      );
    }
  }

  return entry;
};
